import { AppBar, IconButton, Link, MenuItem, Toolbar, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import MenuIcon from '@material-ui/icons/Menu';
import {
  BackArrowIcon,
  NotificationsAlertWhiteIcon,
  SettingsIconTwoTone,
  SignOutIconTwoTone,
  StyledMenu,
} from '@room-match/shared-ui-components';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { RiArrowDownSLine } from 'react-icons/ri';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import CloseIcon from 'shared/components/Icon/CloseIcon';
import { ROUTES } from 'shared/constants/ROUTES';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { impersonate, userLogout } from 'shared/redux/slicers/user.slicer';
import { useAppSelector } from 'shared/redux/store';
import { setDataAttributeDisplay } from '../../lib/sentry';
import { BackButton, CustomIconButton, UserContainerButton, useStyles } from './Header.style';

type Props = {
  isDrawerCollapse?: boolean;
  handleDrawerToggle: () => void;
  browserWarningOpen: boolean;
  setBrowserWarningOpen: Dispatch<SetStateAction<boolean>>;
};

const Header: React.FC<Props> = ({
  isDrawerCollapse,
  handleDrawerToggle,
  browserWarningOpen,
  setBrowserWarningOpen,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const { user: userState, app: appState } = useAppSelector((state) => state);
  const { guardianUI } = appState.guardian;
  const { members } = appState.guardian;
  const { headerTitle, showHeaderBack, headerBackCount } = appState;
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const location = useLocation();
  const [isSettings, setIsSettings] = useState<boolean>(false);

  const userAgent = navigator.userAgent.toLowerCase();
  const isChomeOnIOS = userAgent.includes('crios');
  const isEdgeBrowser = userAgent.includes('edg');
  const isNotChrome = !isChomeOnIOS && (!userAgent.includes('chrome') || isEdgeBrowser);
  const isFirefoxBrowser = userAgent.includes('firefox');
  const isSafariBrowser = isNotChrome && !isEdgeBrowser && userAgent.includes('safari');
  const currBrowser = isFirefoxBrowser
    ? 'Firefox'
    : isEdgeBrowser
      ? 'Edge'
      : isSafariBrowser
        ? 'Safari'
        : 'a browser that is not Chrome';

  const handleLogOut = async () => {
    await queryClient.cancelQueries();
    if ((window as any).FB) (window as any).FB.logout();
    await dispatch(userLogout());
    queryClient.clear();
    // always make sure that localstorage is cleared
    await localStorage.clear();
    // window.location.href = `https://www.google.com/accounts/Logout?continue=https://appengine.google.com/_ah/logout?continue=${window.location.origin}/login`;
    setDataAttributeDisplay(false);
    Sentry.getCurrentScope().setUser(null);
    history.push('/login');
  };

  const handleBack = () => {
    if (location.pathname.includes('profiles') || location.pathname.includes('representatives')) {
      return history.push(
        `/${location.pathname
          .split('/')
          .slice(1, location.pathname.split('/').length - 2)
          .join('/')}`,
      );
    } else if (location.pathname.includes('past-self-tapes')) {
      return history.goBack();
    }
    history.push(
      `/${location.pathname
        .split('/')
        .slice(1, headerBackCount !== undefined ? headerBackCount : location.pathname.split('/').length - 1)
        .join('/')}`,
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    history.push(`${ROUTES.TALENT.SETTINGS}/general_information`);
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.pathname.includes('settings')) {
      setIsSettings(true);
    } else {
      setIsSettings(false);
    }
  }, [location]);

  const impersonateTalent = (id: string) => {
    dispatch(impersonate(id));
    handleClose();
  };

  const talentName = guardianUI
    ? `${userState?.user?.attributes.first_name} ${userState?.user?.attributes.last_name}`
    : userState?.user?.attributes.stage_name;

  return (
    <AppBar
      position="fixed"
      className={clsx(classes.appBar, {
        [classes.appBarShift]: true,
      })}
    >
      {isNotChrome && browserWarningOpen && (
        <div className={classes.browserWarning}>
          WARNING: You're currently using {currBrowser}. Casting.com works best on Chrome.&nbsp;
          <Link href="https://www.google.com/chrome/" target="__blank">
            Download chrome here.
          </Link>
          <IconButton onClick={() => setBrowserWarningOpen(false)} className={classes.browserWarningCloseBtn}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </div>
      )}
      <Toolbar style={{ minHeight: '60px', padding: '0px 10px 0px 0px' }}>
        <CustomIconButton
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          edge="start"
          className={clsx(classes.menuButton, {})}
        >
          <MenuIcon />
        </CustomIconButton>
        {showHeaderBack && (
          <BackButton onClick={handleBack}>
            <BackArrowIcon />
          </BackButton>
        )}

        <Typography
          variant="body1"
          style={{ marginLeft: matches ? 15 : 0, color: theme.palette.type === 'dark' ? '#fff' : '#000' }}
        >
          {headerTitle}
        </Typography>
        <div style={{ flexGrow: 1 }} />
        {headerTitle === 'Dashboard' &&
          (!matches ? (
            <>
              <a href="https://support.castingapp.com/support/home" target="_blank">
                <HelpOutline color="primary" style={{ marginInline: 5, width: 20, height: 20, marginTop: 5 }} />
              </a>
            </>
          ) : (
            <a href="https://support.castingapp.com/support/home" target="_blank" className={classes.getHelpBtn}>
              <HelpOutline style={{ marginRight: 5, width: 20, height: 20 }} />
              Get Help?
            </a>
          ))}
        <div className={classes.sectionDesktop} style={{ minHeight: '60px' }}>
          <UserContainerButton
            isActive={isSettings}
            onClick={handleClick}
            style={{
              padding: `${members.length > 1 ? '0 40px 0 20px' : '0 20px'}`,
            }}
            // endIcon={

            // }
          >
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <Typography
                variant="body2"
                style={{
                  textTransform: 'capitalize',
                  fontSize: 15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 155,
                }}
              >
                {talentName}
              </Typography>
              <CustomTypography fontSize={13}>My Account</CustomTypography>
            </div>
            <img
              src={userState?.user?.attributes.picture || '/assets/user.png'}
              style={{
                width: 34,
                height: 34,
                marginLeft: 'auto',
                borderRadius: 8,
                objectFit: 'cover',
                objectPosition: 'top',
              }}
            />
            {members.length > 1 ? (
              <IconButton
                style={{
                  position: 'absolute',
                  right: 5,
                  width: 30,
                  height: 30,
                  zIndex: 10,
                  padding: 5,
                  borderRadius: 5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
              >
                <RiArrowDownSLine />
              </IconButton>
            ) : (
              <></>
            )}
          </UserContainerButton>
          <CustomIconButton>
            <NotificationsAlertWhiteIcon />
          </CustomIconButton>
          {/* <CustomIconButton onClick={() => history.push(`${ROUTES.TALENT.SETTINGS}/contact_information`)}>
            <SettingsIconTwoTone />
          </CustomIconButton> */}

          <CustomIconButton
            data-cy="button-logout"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleLogOut}
            isLogout={true}
          >
            <SignOutIconTwoTone />
          </CustomIconButton>
        </div>
        <div className={classes.sectionMobile} style={{ minHeight: '60px' }}>
          <CustomIconButton onClick={() => history.push(`${ROUTES.TALENT.SETTINGS}/contact_information`)}>
            <SettingsIconTwoTone />
          </CustomIconButton>
          <CustomIconButton>
            <NotificationsAlertWhiteIcon />
          </CustomIconButton>
          <CustomIconButton
            data-cy="button-logout-mobile"
            aria-label="show more"
            aria-controls="primary-search-account-menu-mobile"
            aria-haspopup="true"
            onClick={handleLogOut}
          >
            <SignOutIconTwoTone />
          </CustomIconButton>
        </div>
        {members.length > 1 ? (
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right', // aligns items to the right of button
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 245, //item growth starts from its top-right corner options ['center', 'left', 'right', or number]
            }}
            PaperProps={{
              style: {
                marginTop: 45,
                minWidth: '150px',
                width: '250px',
                borderRadius: '0 0 8px 8px',
              },
            }}
          >
            {members
              .filter((f) => f.id !== userState?.user?.id)
              .map((f) => (
                <MenuItem onClick={() => impersonateTalent(f.id)} disableRipple style={{ minWidth: 200 }}>
                  <img
                    src={f.picture || '/assets/user.png'}
                    style={{
                      width: 34,
                      height: 34,
                      marginRight: 10,
                      borderRadius: 4,
                      objectFit: 'cover',
                      objectPosition: 'top',
                    }}
                  />
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {f.first_name} {f.last_name}
                  </Typography>
                </MenuItem>
              ))}
          </StyledMenu>
        ) : (
          <></>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
